
import { Component, Mixins, Ref } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { getAllAlternateUrls, getCanonicalUrl } from '~/helpers/metadata';
import GrossNetCalculator from '@hokify/shared-components/lib/components/GrossNetCalculator.vue';
import { AllowedGNCRemunerations } from '~/data/gnc-allowed-path-params';
import type {
	IAPIGrossNetCalculation,
	GNCPeriod,
	IAPILoginUser,
	IGrossNetCalculatorParams
} from '@hokify/common';

import ArticleCard from '~/components/website/ArticleCard.vue';
import ContentMediaSplitComponent from '~/components/website/ContentMediaSplitComponent.vue';
import LoginBase from '~/mixins/LoginBase';
import type { IAPIArticle } from '@hokify/magazine-interface';

const gncStore = namespace('gnc');
const landingPageStore = namespace('user/landingpage');
const loginStore = namespace('login');
const userStore = namespace('user/profile');

@Component({
	name: 'BruttoNettoRechnerPage',
	layout: 'plain-user',
	components: {
		GrossNetCalculator,
		ArticleCard,
		ContentMediaSplitComponent
	},

	// only allow at & de and then check if there is no remuneration, or it is allowed for the country
	validate({ params, store }) {
		return (
			['at', 'de'].includes(store.state.topLevelDomain) &&
			(!params.remuneration ||
				AllowedGNCRemunerations[store.state.topLevelDomain].includes(params.remuneration))
		);
	},

	head(this: BruttoNettoRechnerPage) {
		const remunerationParam =
			this.$route.params.remuneration && Number(this.$route.params.remuneration);
		const path = `${this.$store.state.host}/lp/brutto-netto-rechner${
			remunerationParam ? `/${remunerationParam}` : ''
		}`;

		const canonical = getCanonicalUrl(path);
		const alternates = getAllAlternateUrls(path);
		let description =
			'Brutto in Netto: Berechne, wie viel Gehalt und Sonderzahlungen dir nach Lohnsteuer und Sozialversicherung übrig bleiben!';
		let remunerationObj;
		if (remunerationParam && this.initialGncCalculation) {
			remunerationObj =
				remunerationParam < 10000
					? this.initialGncCalculation.month
					: this.initialGncCalculation.year;
			description = `${remunerationParam}€ brutto sind ${remunerationObj.net}€ netto. Berechne Gehalt und Sonderzahlungen nach Abzug der Lohnsteuer und Sozialversicherung!`;
		}

		const result: any = {
			title: remunerationParam
				? `${remunerationParam} € brutto in netto - Gehaltsrechner`
				: `Brutto-Netto-Rechner | hokify`,
			link: [canonical, ...alternates],
			meta: [
				{ hid: 'robots', name: 'robots', content: 'index, follow' },
				{
					hid: 'description',
					name: 'description',
					content: description
				}
			]
		};

		return result;
	},
	async asyncData({ params, store, error }) {
		const remuneration = params.remuneration && Number(params.remuneration);
		const period = !remuneration || remuneration < 10000 ? 3 : 0;
		const gncCalculation =
			(remuneration &&
				(await store.dispatch('gnc/sendGnc', {
					remuneration,
					period,
					year: new Date().getFullYear(),
					locationUrl: 'lp'
				}))) ||
			undefined;

		const articleAliases = [
			'arbeitsmarkt/insiderwissen-das-bedeutet-rezession-für-den-buerger',
			store.state.topLevelDomain === 'at'
				? 'zulagen-zuschläge-wie-viel-geld-steht-mir-zu'
				: 'arbeitsrecht/zulagen-zuschlaege',
			'jobsuche/bestbezahlten-jobs-ohne-ausbildung'
		];

		const articles: IAPIArticle[] = [];
		try {
			const { articles: loadedArticles } = await store.dispatch('magazine/loadArticlesByAliases', {
				aliases: articleAliases
			});
			articles.push(...loadedArticles);
		} catch (err: any) {
			error(err);
		}

		const randomRemunerationsFirst =
			(store.state.topLevelDomain === 'at' &&
				AllowedGNCRemunerations[store.state.topLevelDomain]
					.sort(() => Math.random() - Math.random())
					.slice(0, 9)) ||
			undefined;
		const randomRemunerationsSecond =
			(store.state.topLevelDomain === 'at' &&
				AllowedGNCRemunerations[store.state.topLevelDomain]
					.sort(() => Math.random() - Math.random())
					.slice(10, 19)) ||
			undefined;

		return {
			remuneration,
			period,
			gncCalculation,
			initialGncCalculation: gncCalculation,
			articles,
			randomRemunerationsFirst,
			randomRemunerationsSecond
		};
	}
})
export default class BruttoNettoRechnerPage extends Mixins(LoginBase({ uref: 'gnc' })) {
	@gncStore.Action('sendGnc') sendGnc;

	@landingPageStore.Getter('getMappedNewJobs') getMappedNewJobs;

	@loginStore.State('loggedIn') loggedIn;

	@userStore.State('obj') user!: IAPILoginUser;

	@Ref('gncPopupHandler') readonly gncPopupHandler?: { handleClick: () => void };

	mode = 'login';

	gncData: IGrossNetCalculatorParams | undefined;

	gncCalculation: IAPIGrossNetCalculation | undefined;

	initialGncCalculation: IAPIGrossNetCalculation | undefined;

	remuneration: number | undefined = undefined;

	period: GNCPeriod = 3;

	articles!: IAPIArticle[];

	linkBoxes = [
		{
			icon: 'icon-money-gradient',
			headline: 'Der Unterschied zwischen brutto und netto erklärt?',
			text:
				this.$store.state.topLevelDomain === 'at'
					? 'Erfahre, wie viel 2000 € brutto in netto umgerechnet sind!'
					: 'Erfahre, warum du netto weniger verdienst und in welcher Steuerklasse du bist!',
			link:
				this.$store.state.topLevelDomain === 'at'
					? '/karriere/arbeitsmarkt/brutto-netto'
					: '/karriere/arbeitsrecht/brutto-netto-erkl%C3%A4rt'
		},
		{
			icon: 'icon-lightbulb-gradient',
			headline: 'Gehaltsverhandlung rocken',
			text: ' Mit diesen Tipps meisterst du jede Gehaltsverhandlung und holst dir deine Gehaltserhöhung! ',
			link: '/karriere/arbeitsrecht/gehalt-ist-verhandlungssache-so-gehst-du-vor'
		},
		{
			icon: 'icon-calculator-gradient',
			headline: 'Alles wird teurer!',
			text: 'Erfahre, was du tun kannst, um die Inflation zu besiegen und dein Brutto-Einkommen zu erhöhen.',
			link: '/karriere/arbeitsmarkt/tipps-gegen-die-inflation-so-erh%C3%B6hst-du-dein-einkommen'
		}
	];

	profileCard = {
		type: 'img',
		mediaPosition: 'left',
		src: 'img/b2c-lps/gnc/gnc-find-job.png',
		title: 'Jetzt den perfekten Job finden!',
		text: 'Jobsuche und Bewerbung ist mit hokify so einfach wie nie! Finde jetzt innerhalb weniger Minuten deinen Traumjob oder lass uns deinen Arbeitsalltag mit allen notwendigen Tipps und Tools erleichtern!',
		buttonText: 'Profil erstellen',
		buttonLink: '/pwa/profile'
	};

	quizCard = {
		type: 'img',
		mediaPosition: 'right',
		src: 'img/b2c-lps/gnc/gnc-jobinterviewtraining.png',
		title: 'Bist du bereit?',
		text: 'Die richtige Vorbereitung ist das Wichtigste beim Vorstellungsgespräch. Teste jetzt deine Skills mit dem hokify Vorstellungsgespräch-Check und übe für das Bewerbungsgespräch! Kannst du die 10 häufigsten Fragen im Vorstellungsgespräch richtig beantworten?',
		buttonText: 'Quiz starten',
		buttonLink: '/lp/jobinterviewtraining'
	};

	gncExampleHeaderDesktop = [
		'Bruttolohn',
		'Lohnsteuer',
		'Sozialversicherung',
		'Urlaubszuschuss',
		'Weihnachtsremuneration',
		'Nettolohn'
	];

	gncExamplesDesktop = [
		[
			"<a href='/lp/brutto-netto-rechner/1800' target='_blank' class='font-bold'>1.800,00 €</a>",
			'<span class="text-color-grey">51,18€</span>',
			'<span class="text-color-grey">272,16 €</span>',
			'<span class="text-color-grey">1.490,29 €</span>',
			'<span class="text-color-grey">1.453,09 €</span>',
			'<strong class="text-color-grey font-bold">1.476,66 €</strong>'
		],
		[
			"<a href='/lp/brutto-netto-rechner/2000' target='_blank' class='font-bold'>2.000,00 €</a>",
			'<span class="text-color-grey">81,14 €</span>',
			'<span class="text-color-grey">322,40 €</span>',
			'<span class="text-color-grey">1.632,94 €</span>',
			'<span class="text-color-grey">1.595,74 €</span>',
			'<span class="text-color-grey font-bold">1.596,46 €</span>'
		],
		[
			"<a href='/lp/brutto-netto-rechner/2400' target='_blank' class='font-bold'>2.400,00 €</a>",
			'<span class="text-color-grey">160,93 €</span>',
			'<span class="text-color-grey">433,68 €</span>',
			'<span class="text-color-grey">1.908,10 €</span>',
			'<span class="text-color-grey">1.870,9\0 €</span>',
			'<strong class="text-color-grey font-bold">1.805,39 €</strong>'
		],
		[
			"<a href='/lp/brutto-netto-rechner/2500' target='_blank' class='font-bold'>2.500,00 €</a> ",
			'<span class="text-color-grey">185,51 €</span>',
			'<span class="text-color-grey">451,75 €</span>',
			'<span class="text-color-grey">1.986,05 €</span>',
			'<span class="text-color-grey">1948,85 €</span>',
			'<strong class="text-color-grey font-bold">1,862,74 €</strong>'
		],
		[
			"<a href='/lp/brutto-netto-rechner/2800' target='_blank' class='font-bold'>2.800,00 €</a> ",
			'<span class="text-color-grey">259,25 €</span>',
			'<span class="text-color-grey">505,96 €</span>',
			'<span class="text-color-grey">2.219,92 €</span>',
			'<span class="text-color-grey">2.182,72 €</span>',
			'<strong class="text-color-grey font-bold">2.034,79 €</strong>'
		],
		[
			"<a href='/lp/brutto-netto-rechner/3000' target='_blank' class='font-bold'>3.000,00 €</a> ",
			'<span class="text-color-grey">308,40 €</span>',
			'<span class="text-color-grey">542,10 €</span>',
			'<span class="text-color-grey">2.375,83 €</span>',
			'<span class="text-color-grey">2.338,63 €</span>',
			'<strong class="text-color-grey font-bold">2.149,50 €</strong>'
		]
	];

	gncExampleHeaderMobile = ['Bruttolohn', 'Nettolohn'];

	gncExamplesMobile = [
		[
			"<a href='/lp/brutto-netto-rechner/1800' target='_blank' class='font-bold'>1.800,00 €</a>",

			'1.476,66 €'
		],
		[
			"<a href='/lp/brutto-netto-rechner/2000' target='_blank' class='font-bold'>2.000,00 €</a>",

			'1.596,46 €'
		],
		[
			"<a href='/lp/brutto-netto-rechner/2400' target='_blank' class='font-bold'>2.400,00 €</a>",

			'1.805,39 €'
		],
		[
			"<a href='/lp/brutto-netto-rechner/2500' target='_blank' class='font-bold'>2.500,00 €</a> ",

			'1,862,74 €'
		],
		[
			"<a href='/lp/brutto-netto-rechner/2800' target='_blank' class='font-bold'>2.800,00 €</a> ",

			'2.034,79 € '
		],
		[
			"<a href='/lp/brutto-netto-rechner/3000' target='_blank' class='font-bold'>3.000,00 €</a> ",
			'2.034,79 €'
		]
	];

	gncHeaderGermany = ['I', 'II', 'III', 'IV', 'V', 'VI', ''];

	gncGermanyGross = [
		"<a href='/jobs/search?filters=wage-0-520.0' target='_blank' class='font-bold'>500,00 €</a>",
		"<a href='/jobs/search?filters=wage-520.0-850.0' target='_blank' class='font-bold'>800,00 €</a>",
		"<a href='/jobs/search?filters=wage-850.0-1200.0' target='_blank' class='font-bold'>1.000,00 €</a>",
		"<a href='/jobs/search?filters=wage-1200.0-2500.0' target='_blank' class='font-bold'>1.500,00 €</a>",
		"<a href='/jobs/search?filters=wage-1200.0-2500.0' target='_blank' class='font-bold'>2.000,00 €</a>",
		"<a href='/jobs/search?filters=wage-1200.0-2500.0' target='_blank' class='font-bold'>2.500,00 €</a>",
		"<a href='/jobs/search?filters=wage-2500.0-5300.0' target='_blank' class='font-bold'>3.000,00 €</a>",
		"<a href='/jobs/search?filters=wage-2500.0-5300.0' target='_blank' class='font-bold'>3.500,00 €</a>",
		"<a href='/jobs/search?filters=wage-2500.0-5300.0' target='_blank' class='font-bold'>4,000,00 €</a>",
		"<a href='/jobs/search?filters=wage-2500.0-5300.0' target='_blank' class='font-bold'>4.500,00 €</a>",
		"<a href='/jobs/search?filters=wage-2500.0-5300.0' target='_blank' class='font-bold'>5.000,00 €</a>",
		"<a href='/jobs/search?filters=wage-5300.0-0' target='_blank' class='font-bold'>5.500,00 €</a>"
	];

	gncNetGermany = [
		{
			link: '/jobs/search?filters=wage-0-520.0',
			taxClasses: [
				'<span class="text-color-grey">392,25 €</span>',
				'<span class="text-color-grey">392,25 €</span>',
				'<span class="text-color-grey">392,25 €</span>',
				'<span class="text-color-grey">392,25 €</span>',
				'<span class="text-color-grey">352,00 €</span>',
				'<span class="text-color-grey">337,17 €</span>'
			]
		},
		{
			link: '/jobs/search?filters=wage-520.0-850.0',
			taxClasses: [
				'<span class="text-color-grey">692,72 €</span>',
				'<span class="text-color-grey">692,72 €</span>',
				'<span class="text-color-grey">692,72 €</span>',
				'<span class="text-color-grey">692,72 €</span>',
				'<span class="text-color-grey">619,39 €</span>',
				'<span class="text-color-grey">604,64 €</span>'
			]
		},
		{
			link: '/jobs/search?filters=wage-850.0-1200.0',
			taxClasses: [
				'<span class="text-color-grey">838,78 €</span>',
				'<span class="text-color-grey">838,78 €</span>',
				'<span class="text-color-grey">838,78 €</span>',
				'<span class="text-color-grey">838,78 €</span>',
				'<span class="text-color-grey">743,45 €</span>',
				'<span class="text-color-grey">728,62 €</span>'
			]
		},
		{
			link: '/jobs/search?filters=wage-1200.0-2500.0',
			taxClasses: [
				'<span class="text-color-grey">1.186,64 €</span>',
				'<span class="text-color-grey">1.203,89 €</span>',
				'<span class="text-color-grey">1.203,89 €</span>',
				'<span class="text-color-grey">1.186,64 €</span>',
				'<span class="text-color-grey">1.050,56 €</span>',
				'<span class="text-color-grey">1.013,06 €</span>'
			]
		},
		{
			link: '/jobs/search?filters=wage-1200.0-2500.0',
			taxClasses: [
				'<span class="text-color-grey">1.467,50 €</span>',
				'<span class="text-color-grey">1.544,00 €</span>',
				'<span class="text-color-grey">1.569,00 €</span>',
				'<span class="text-color-grey">1.467,50 €</span>',
				'<span class="text-color-grey">1.254,42 €</span>',
				'<span class="text-color-grey">1.210,09 €</span>'
			]
		},
		{
			link: '/jobs/search?filters=wage-1200.0-2500.0',
			taxClasses: [
				'<span class="text-color-grey">1.759,00 €</span>',
				'<span class="text-color-grey">1.848,75 €</span>',
				'<span class="text-color-grey">1.961,25 €</span>',
				'<span class="text-color-grey">1.759,00 €</span>',
				'<span class="text-color-grey">1.478,50 €</span>',
				'<span class="text-color-grey">1.434,25 €</span>'
			]
		},
		{
			link: '/jobs/search?filters=wage-2500.0-5300.0',
			taxClasses: [
				'<span class="text-color-grey">2.043,50 €</span>',
				'<span class="text-color-grey">2.139,42 €</span>',
				'<span class="text-color-grey">2.300,84 €</span>',
				'<span class="text-color-grey">2.043,50 €</span>',
				'<span class="text-color-grey">1.715,34 €</span>',
				'<span class="text-color-grey">1.675,00 €</span>'
			]
		},
		{
			link: '/jobs/search?filters=wage-2500.0-5300.0',
			taxClasses: [
				'<span class="text-color-grey">2.321,00 €</span>',
				'<span class="text-color-grey">2.423,09 €</span>',
				'<span class="text-color-grey">2.612,59 €</span>',
				'<span class="text-color-grey">2.321,00 €</span>',
				'<span class="text-color-grey">1.949,59 €</span>',
				'<span class="text-color-grey">1.905,50 €</span>'
			]
		},
		{
			link: '/jobs/search?filters=wage-2500.0-5300.0',
			taxClasses: [
				'<span class="text-color-grey">2.591,59 €</span>',
				'<span class="text-color-grey">2.699,84 €</span>',
				'<span class="text-color-grey">2.909,34 €</span>',
				'<span class="text-color-grey">2.591,59 €</span>',
				'<span class="text-color-grey">2.174,00 €</span>',
				'<span class="text-color-grey">2.129,67 €</span>'
			]
		},
		{
			link: '/jobs/search?filters=wage-2500.0-5300.0',
			taxClasses: [
				'<span class="text-color-grey">2.855,17 €</span>',
				'<span class="text-color-grey">2.969,59 €</span>',
				'<span class="text-color-grey">3.201,59 €</span>',
				'<span class="text-color-grey">2.855,17 €</span>',
				'<span class="text-color-grey">2.398,09 €</span>',
				'<span class="text-color-grey">2.353,84 €</span>'
			]
		},
		{
			link: '/jobs/search?filters=wage-2500.0-5300.0',
			taxClasses: [
				'<span class="text-color-grey">3.111,75 €</span>',
				'<span class="text-color-grey">3.232,42 €</span>',
				'<span class="text-color-grey">3.490,34 €</span>',
				'<span class="text-color-grey">3.111,75 €</span>',
				'<span class="text-color-grey">2.622,25 €</span>',
				'<span class="text-color-grey">2.577,92 €</span>'
			]
		},
		{
			link: '/jobs/search?filters=wage-5300.0-0',
			taxClasses: [
				'<span class="text-color-grey">3.361,42 €</span>',
				'<span class="text-color-grey">3.488,25 €</span>',
				'<span class="text-color-grey">3.775,75 €</span>',
				'<span class="text-color-grey">3.361,42 €</span>',
				'<span class="text-color-grey">2.846,42 €</span>',
				'<span class="text-color-grey">2.801,88 €</span>'
			]
		}
	];

	randomRemunerationsFirst!: string[] | undefined;

	randomRemunerationsSecond!: string[] | undefined;

	async calcGnc(gnc) {
		try {
			this.gncCalculation = await this.sendGnc({ ...gnc, locationUrl: 'lp' });
			this.$trackWebsiteEvent('start_calculation', {});
		} catch (err: any) {
			this.$errorHandler.call(this, err);
		}
	}

	openJobsearch() {
		this.$router.push('/jobs');
		this.$trackWebsiteEvent('click_redirect', { url: '/jobs' });
	}

	updateRemuneration(updatedRemuneration) {
		this.remuneration = updatedRemuneration;
	}

	updatePeriod(updatedPeriod) {
		this.period = updatedPeriod;
	}
}
